import pkg from "../lib/pkg";
import { injectScript } from "../lib/util";
import {
  Integration,
  IntegrationCategory,
} from "../providers/AnalyticsContext";
import type { Context } from "../types";
import { AnalyticsPlugin, AnalyticsPluginType } from ".";

type OpenAiAdsContentsData = {
  type: "contents";
  amount?: number;
  currency?: string;
};

type OpenAiAdsEventOptions = {
  event_id?: string;
};

type OpenAiAdsCommandArgs =
  | ["init", { pixelId: string }]
  | [
      "measure",
      "contents_viewed" | "order_created" | "page_viewed",
      OpenAiAdsContentsData,
      OpenAiAdsEventOptions?,
    ]
  | ["measure", "registration_completed", { type: "customer_action" }]
  | [
      "measure",
      "custom",
      { type: "custom" },
      OpenAiAdsEventOptions & { custom_event_name: "newbuyer" },
    ];

type OpenAiAdsQueue = {
  (...args: OpenAiAdsCommandArgs): void;
  q: IArguments[];
};

declare global {
  interface Window {
    oaiq?: OpenAiAdsQueue;
  }
}

export default class OpenAiAdsPlugin implements AnalyticsPlugin {
  name = Integration.OpenAiAds;
  category = IntegrationCategory.Advertising;
  type = AnalyticsPluginType.destination;
  version = "0.1.0";

  scriptId = "os-openai-ads";
  scriptSrc = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

  constructor(private pixelId = pkg.openAiAdsPixelId) {}

  isLoadable() {
    return Boolean(this.pixelId);
  }

  async load(): Promise<void> {
    if (!this.pixelId) {
      return;
    }

    if (!window.oaiq) {
      const queuedCalls: IArguments[] = [];
      const queue: OpenAiAdsQueue = Object.assign(
        function (..._args: OpenAiAdsCommandArgs) {
          queuedCalls.push(arguments);
        },
        { q: queuedCalls },
      );
      window.oaiq = queue;
    }

    const scriptLoaded = injectScript(
      this.scriptId,
      this.scriptSrc,
      undefined,
      document.head,
    );
    window.oaiq("init", { pixelId: this.pixelId });
    await scriptLoaded;
  }

  async unload(): Promise<void> {
    document.getElementById(this.scriptId)?.remove();
    window.oaiq = undefined;
  }

  isLoaded() {
    return Boolean(
      typeof window !== "undefined" &&
        window.oaiq &&
        document.getElementById(this.scriptId),
    );
  }

  async track(context: Context): Promise<Context> {
    const properties = context.event.properties as
      | Record<string, unknown>
      | undefined;

    switch (context.event.event) {
      case "Order Completed":
      case "Gift Card Purchased":
        this.measureOrder(dollarsToCents(properties?.total), properties);
        break;
      case "user-created":
        window.oaiq?.("measure", "registration_completed", {
          type: "customer_action",
        });
        break;
      case "Product Viewed":
        window.oaiq?.("measure", "contents_viewed", { type: "contents" });
        break;
      case "New Buyer Acquired":
        // Membership checkout also emits this generic event, but Marketing
        // intentionally excludes membership conversions from OpenAI Ads.
        if (properties?.isSubscription === true) {
          break;
        }
        this.measureNewBuyer(properties);
        break;
      case "New Gift Card Buyer Acquired":
        this.measureNewBuyer(properties);
        break;
    }

    return context;
  }

  async page(context: Context): Promise<Context> {
    window.oaiq?.("measure", "page_viewed", { type: "contents" });

    return context;
  }

  private measureOrder(
    amount: number | undefined,
    properties: Record<string, unknown> | undefined,
  ) {
    if (isFreeTrial(properties)) {
      return;
    }

    const data = buildContentsData(amount, getString(properties?.currency));
    const eventId = getString(properties?.order_id);

    if (eventId) {
      window.oaiq?.("measure", "order_created", data, {
        event_id: eventId,
      });
    } else {
      window.oaiq?.("measure", "order_created", data);
    }
  }

  private measureNewBuyer(properties: Record<string, unknown> | undefined) {
    if (isFreeTrial(properties)) {
      return;
    }

    const eventId = getString(properties?.order_id);

    window.oaiq?.(
      "measure",
      "custom",
      { type: "custom" },
      {
        custom_event_name: "newbuyer",
        ...(eventId ? { event_id: eventId } : {}),
      },
    );
  }
}

// A membership free trial collects no payment at signup, so it is neither a
// purchase nor a first purchase.
function isFreeTrial(properties: Record<string, unknown> | undefined): boolean {
  return (
    properties?.is_free_trial === true || properties?.is_free_trial === "true"
  );
}

// OpenAI requires `currency` whenever `amount` is present, so they go together
// or not at all.
function buildContentsData(
  amount: number | undefined,
  currency: string | undefined,
): OpenAiAdsContentsData {
  return amount !== undefined && currency
    ? { type: "contents", amount, currency }
    : { type: "contents" };
}

function dollarsToCents(value: unknown): number | undefined {
  const dollars = getNumber(value);
  return dollars === undefined ? undefined : Math.round(dollars * 100);
}

function getNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function getString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}
